import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAuthData } from "../../../services/storage/LocalStorage";
import { createConversation, clearSelectedConversation } from "../../../state/actions/chats";
import { setHideChatwidget, setHideDefaultHomepageBot } from "../../../state/actions/bot";
import { getPageName } from "../../../utils/general";
import {fullBotScriptReset, spinnetScriptSrc, sendParamsToSpinnet, loadSpinnetScriptStyles} from "../../../utils/ChatbotScriptHelpers";

import FrontmGifLogoLoader from "../../ui/Loader/frontmGifLogoLoader";
import { SOCKET } from "../../../services/socket/initWebSocket";
import { useNavigate } from "react-router-dom";
import { useOnlineStatus } from "../../../services/customHooks/useOnlineStatus";
import { withOutInitSocket } from "../../../state/actions/socket";
import { createConversationId } from "../../../utils/conversationUtils";
import { startConversation } from "../../../services/socket/socketEvents";
import { setBotWaitLoader, setBotWaitLoaderWithShimmer } from "../../../state/actions/loader";
import { setSelectedBot, removeBot } from "../../../state/actions/domain";


const SpinnetView = ({
  isLoading,
  baseUrl,
  botId,
  email,
  mobileSession,
  appType,
  platform = "web",
  conversationId,
  userDomain = "onship",
  version = "1.0.0",
  isDefaultHomePageBot,
  botSubscriptions,
  refreshToken,
  connectionId
}) => {
   
  baseUrl = baseUrl.includes('https') ? baseUrl : `https://${baseUrl}`
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authData = getAuthData();
  const selectedDomain = useSelector((state) => state.domain?.selectedDomain);
  const clearCacheForSpinnetBot = useSelector((state) => state.bot?.clearCacheForSpinnetBot);
  const [initialClearCache] = useState(clearCacheForSpinnetBot);
  const isUserOnline = useOnlineStatus();
  const [loader, setLoader] = useState(true);
  let bots = botSubscriptions; 

  const styleId = "spinnet-as-widget-styles";
  const stylePath = "/scripts/spinnet/static/css/main.487c5bb6.css";

  useEffect(() => {
      if (loader) {
        const minDelay = setTimeout(() => setLoader(false), 8000);
        return () => clearTimeout(minDelay);
      }
    }, [loader]);

  useEffect(() => {
    performCleanupAndLoad();
  }, [selectedDomain, isUserOnline]);


  const performCleanupAndLoad = async () => {
    // Clear the spinnet root element for fresh load
    const spinnetRoot = document.getElementById("spinnet-root");
    if (spinnetRoot) {
      spinnetRoot.innerHTML = "";
    }
    
    // Destroy existing spinnet widget if available
    try {
      if (typeof window.destroyChatWidget === "function") {
        window.destroyChatWidget();
      }
    } catch (err) {
      console.warn("Error while calling destroyChatWidget:", err);
    }
    
    const response = await fullBotScriptReset();
    if (response === true) {
      setTimeout(() => {
        loadSpinnetScriptStyles(styleId, stylePath);
        loadBotScript();
      }, 300);
    }
  };

  const loadBotScript = () => {
    // Remove existing spinnet script if present
    const existingScript = document.querySelector(`script[src="${spinnetScriptSrc}"]`);
    if (existingScript && existingScript.parentNode) {
      existingScript.parentNode.removeChild(existingScript);
    }
    
    setTimeout(() => {
      const script = document.createElement('script');
      script.src = spinnetScriptSrc;
      script.async = true;
      const nativeEnv = process.env.REACT_APP_ENV || 'development';
      script.onload = () => {
        // console.log("Chatwidget Script loaded successfully.");
        setTimeout(() => {
          const params = {
            email: email,
            mobileSession: mobileSession,
            appType: appType,
            conversationId: conversationId,
            platform: platform,
            botId: botId,
            version: version,
            userDomain: userDomain,
            isHomepage: isDefaultHomePageBot ? "true" : "false",
            clearCache: isDefaultHomePageBot ? String(initialClearCache) : "false",
            connectionId: connectionId,
            refreshToken: refreshToken,
            ws: SOCKET.ws,
            nativeEnv
          };
          sendParamsToSpinnet(params);
          dispatch(setBotWaitLoaderWithShimmer(false));
          dispatch(setBotWaitLoader(false));
        }, 200);
      };
      script.onerror = (e) => {
        console.error("Error loading the script: ", e);
        setLoader(false)
      };
      document.body.appendChild(script);
    }, 100);
  };

  useEffect(() => {
  window.bannerClick = async (value) => {
    console.log('handleBannerClick', value);

    const userId = authData?.user?.userId;

    const redirectMap = {
      Apps: "/app/catalog",
      Contacts: "/app/contacts",
      myProfileScreenOnship: "/app/my-profile",
    };

    const target = redirectMap[value?.data];

    if (target) {
      // Clear selected bot and conversation state before navigating
      dispatch(removeBot());
      dispatch(clearSelectedConversation());
      dispatch(setHideDefaultHomepageBot());
      navigate(target);
    }
    else if (value?.type === "bot") {
      if (!bots || bots.length === 0) return;

      dispatch(setHideDefaultHomepageBot());
      dispatch(withOutInitSocket(true));

      const botDetails = bots.find(
        (bot) => bot?.botId === value?.data
      );

      if (botDetails) {
        const userDomain = botDetails.userDomain;

        const conversationId = await createConversationId(
          userId,
          value?.data
        );

        dispatch(
          createConversation(
            userId,
            value?.data,
            userDomain
          )
        );

        startConversation(userId, {
          conversationId,
          botId: value?.data,
          userDomain,
        });

        dispatch(setBotWaitLoader(true));
        dispatch(setSelectedBot(botDetails));
      }
    }
  };

  return () => {
    delete window.bannerClick;
  };
}, [authData, bots, dispatch, navigate]);

useEffect(() => {
  window.handleBotClick = async (value) => {
    console.log("window.botClick called:", value);

    const userId = authData?.user?.userId;
    if (!userId || !value?.botId) return;

    if (value?.customFrontEnd) {
      dispatch(setHideChatwidget());
    }

    dispatch(withOutInitSocket(true));
    dispatch(setHideDefaultHomepageBot());

    const conversationId = await createConversationId(
      userId,
      value?.botId
    );

    dispatch(
      createConversation(
        userId,
        value?.botId,
        selectedDomain
      )
    );

    startConversation(userId, {
      conversationId,
      botId: value?.botId,
      userDomain: selectedDomain,
    });

    dispatch(setBotWaitLoader(true));
    dispatch(setSelectedBot(value));
  };

  return () => {
    delete window.handleBotClick;
  };
}, [authData, dispatch, selectedDomain]);

  return (
    <>
      <div
        
      >

        {loader && (
          <FrontmGifLogoLoader className="!absolute !top-1/2 !left-1/2 !transform !-translate-x-1/2 !-translate-y-1/2 z-[999]" />
        )}
        {/* Chatbot Embed Container */}
        <div className="flex-1 bg-[#FFF]">
          <div id="spinnet-root"></div>
        </div>
      </div>
    </>
  );
};

export default SpinnetView;
